import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowUpRight, ChevronDown, Menu, X } from "lucide-react";
import { navigation } from "../data/navigation";

function NavigationItem({ item, depth = 0 }) {
  const closeTimer = useRef(null);
  useEffect(() => () => window.clearTimeout(closeTimer.current), []);

  const openOnHover = (event) => {
    if (
      depth > 0 ||
      event.pointerType === "touch" ||
      !window.matchMedia?.("(min-width: 801px) and (hover: hover)")?.matches
    )
      return;
    window.clearTimeout(closeTimer.current);
    const group = event.currentTarget;
    const details = group.querySelector(":scope > details");
    Array.from(group.parentElement.children).forEach((sibling) => {
      if (sibling !== group)
        sibling.querySelectorAll("details[open]").forEach((menu) => {
          menu.open = false;
        });
    });
    details.open = true;
  };

  const closeOnLeave = (event) => {
    if (
      depth > 0 ||
      event.pointerType === "touch" ||
      !window.matchMedia?.("(min-width: 801px) and (hover: hover)")?.matches
    )
      return;
    const group = event.currentTarget;
    closeTimer.current = window.setTimeout(() => {
      if (!group.contains(document.activeElement))
        group.querySelectorAll("details[open]").forEach((menu) => {
          menu.open = false;
        });
    }, 120);
  };

  if (!item.submenu)
    return (
      <li>
        <Link to={item.link} target={item.target} rel={item.rel}>
          {item.title}
        </Link>
      </li>
    );
  return (
    <li
      className={`univ-nav-group univ-nav-depth-${depth}`}
      onPointerEnter={openOnHover}
      onPointerLeave={closeOnLeave}
    >
      <details>
        <summary>
          {item.title}
          <ChevronDown size={13} aria-hidden="true" />
        </summary>
        <ul>
          {item.submenu.map((child) => (
            <NavigationItem key={child.title} item={child} depth={depth + 1} />
          ))}
        </ul>
      </details>
    </li>
  );
}

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const header = useRef(null);
  const menuButton = useRef(null);
  const navSlot = useRef(null);
  const navBar = useRef(null);
  const [scrollNav, setScrollNav] = useState({ fixed: false, hidden: false });
  useEffect(() => {
    const slot = navSlot.current;
    const bar = navBar.current;
    let previousY = window.scrollY;
    let frame = 0;
    const update = () => {
      frame = 0;
      const y = Math.max(0, window.scrollY);
      const desktop = window.innerWidth > 800;
      slot.style.height = desktop ? `${bar.offsetHeight}px` : "";
      document.documentElement.style.setProperty(
        "--univ-header-height",
        `${header.current.offsetHeight}px`,
      );
      const threshold = slot.getBoundingClientRect().top + y;
      const fixed = desktop && y >= threshold;
      const delta = y - previousY;
      setScrollNav((current) => ({
        fixed,
        hidden: fixed && current.fixed && !bar.contains(document.activeElement)
          ? (Math.abs(delta) >= 8 ? delta > 0 : current.hidden)
          : false,
      }));
      if (Math.abs(delta) >= 8 || !fixed) previousY = y;
    };
    const schedule = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };
    const observer = typeof ResizeObserver !== "undefined"
      ? new ResizeObserver(schedule) : null;
    observer?.observe(header.current);
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    update();
    return () => {
      window.cancelAnimationFrame(frame);
      observer?.disconnect();
      document.documentElement.style.removeProperty("--univ-header-height");
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
    };
  }, []);
  useEffect(() => {
    if (!window.matchMedia?.("(max-width: 800px)")?.matches) return undefined;
    const previous = document.body.style.overflow;
    if (open) document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);
  useEffect(() => {
    setOpen(false);
    header.current
      ?.querySelectorAll("details[open]")
      .forEach((detail) => detail.removeAttribute("open"));
  }, [location.pathname]);
  useEffect(() => {
    const dismiss = (event) => {
      if (event.type === "keydown" && event.key !== "Escape") return;
      if (
        event.type === "pointerdown" &&
        header.current?.contains(event.target)
      )
        return;
      const desktopSummary = header.current?.querySelector(
        "details[open] > summary",
      );
      header.current
        ?.querySelectorAll("details[open]")
        .forEach((detail) => detail.removeAttribute("open"));
      setOpen(false);
      if (event.type === "keydown") {
        if (
          window.matchMedia?.("(min-width: 801px)")?.matches &&
          desktopSummary
        )
          desktopSummary.focus();
        else menuButton.current?.focus();
      }
    };
    document.addEventListener("pointerdown", dismiss);
    document.addEventListener("keydown", dismiss);
    return () => {
      document.removeEventListener("pointerdown", dismiss);
      document.removeEventListener("keydown", dismiss);
    };
  }, []);
  return (
    <header className="univ-header" ref={header}>
      <a className="univ-skip" href="#main-content">
        Skip to content
      </a>
      <div className="univ-utility">
        <div className="univ-wrap">
          <span>Tindivanam | <a href="tel:+918925969546">+91 8925969546</a></span>
          <Link
            className="univ-announcement"
            to="/induction-day-ceremony"
            aria-label="Announcement: Induction Day Ceremony, December 19, 2025"
          >
            <span aria-hidden="true">
              Announcement: Induction Day Ceremony &middot; December 19, 2025
            </span>
          </Link>
          <div className="univ-utility-links">
            <Link to="/hospital">Hospital</Link>
            <Link to="/pro-fomra">MARB NMC Pro-Forma</Link>
            <Link to="/mci-detail">MSR Clause B.1.11</Link>
            <Link to="/contact-us">
              Contact us <ArrowUpRight size={12} />
            </Link>
          </div>
        </div>
      </div>
      <div className="univ-brand-row univ-wrap">
        <Link
          to="/"
          className="univ-brand"
          aria-label="Takshashila Medical College and Hospital home"
        >
          <img
            src="/assets/images/logo/logo-full.png"
            alt="Takshashila Medical College & Hospital"
          />
        </Link>
        <div className="univ-brand-note">
          A commitment to medicine.
          <br />
          <strong>A lifetime of purpose.</strong>
        </div>
        <Link
          className="univ-button univ-button-primary univ-enquire"
          to="/contact-us"
        >
          Admission enquiries <ArrowUpRight size={17} />
        </Link>
        <button
          ref={menuButton}
          className="univ-menu-toggle"
          aria-label={open ? "Close navigation" : "Open navigation"}
          aria-expanded={open}
          aria-controls="univ-navigation"
          onClick={() => setOpen(!open)}
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>
      <div
        className={`univ-nav-backdrop ${open ? "is-open" : ""}`}
        onClick={() => setOpen(false)}
        aria-hidden="true"
      />
      <Link
        className={`univ-nav-logo ${open ? "is-open" : ""}`}
        to="/"
        aria-label="Takshashila Medical College and Hospital home"
        tabIndex={open ? 0 : -1}
      >
        <img
          src="/assets/images/logo/logo-full.png"
          alt=""
          aria-hidden="true"
        />
      </Link>
      <div ref={navSlot} className="univ-navigation-slot">
      <nav
        ref={navBar}
        className={`univ-navigation ${open ? "is-open" : ""} ${scrollNav.fixed ? "is-fixed" : ""} ${scrollNav.hidden ? "is-scroll-hidden" : ""}`}
        onFocusCapture={() => setScrollNav((current) => ({ ...current, hidden: false }))}
        id="univ-navigation"
        aria-label="Main navigation"
      >
        <div className="univ-wrap">
          <ul>
            {navigation.map((item) => (
              <NavigationItem key={item.title} item={item} />
            ))}
          </ul>
          <div className="univ-nav-secondary">
            <Link to="/pro-fomra">MARB NMC Pro-Forma</Link>
            <Link to="/mci-detail">MSR Clause B.1.11</Link>
          </div>
          <Link className="univ-campus-link" to="/infrastructure">
            Explore our campus <ArrowUpRight size={15} />
          </Link>
          <Link className="univ-nav-contact" to="/contact-us">
            Contact us <ArrowUpRight size={15} />
          </Link>
        </div>
      </nav>
      </div>
    </header>
  );
}
